import React, {
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react'
import Typography from '@material-ui/core/Typography'
import PropTypes from 'prop-types'
import cls from './input-file.module.scss'
import classNames from 'classnames'
import { Button } from '@/shared/ui/buttons/Button/Button'
import InputLabel from '@material-ui/core/InputLabel'
import Icon from '@material-ui/core/Icon'
import { useCustomSnackbar } from '~src/new/shared/lib/useCustomSnackbar'
import { useBool } from '~src/new/shared/lib/useBool'
import { formatFileSize, sizeWithBase64Encoding } from '~src/new/shared/lib/file'

export const InputFile = forwardRef(
  ({ data, onChange, onBlur, onFocus, disabled = false, className, innerRef, ...otherProps }) => {
    const {
      id,
      info,
      errors,
      placeholder = 'Выберите файлы',
      maxFiles = 10,
      maxFileSize = 7 * 1024 * 1024,
      maxOverallFilesSize = 10 * 1024 * 1024,
      required = false,
      multiple = false,
      allowedTypes,
      buttonLabel,
    } = data

    const [currentFiles, setCurrentFiles] = useState([])
    const { value: isFullLoad, onTrue: onFullLoad, onFalse: onNotFullLoad } = useBool(false)
    const { value: isLoading, onTrue: onLoadStart, onFalse: onLoadEnd } = useBool(false)
    const fileInputRef = useRef(null)
    const { enqueueSnackbar } = useCustomSnackbar()

    const requirementsInfoList = useMemo(() => {
      const infoList = Object.entries(info)

      return (
        <div className={cls.requirementsListContainer}>
          <Typography className={cls.placeholder}>Требования:</Typography>
          <ul className={cls.requirementsList}>
            {infoList.map((item, index) => (
              <li key={index}>
                <Typography className={cls.placeholder}>{item[1]}</Typography>
              </li>
            ))}
          </ul>
        </div>
      )
    }, [info])

    const handleFileSelect = useCallback(
      event => {
        let multipleFilesErrorText
        const files = Array.from(event.target.files || [])
        const dt = new DataTransfer()

        try {
          if (files.length === 0) return

          if (!multiple && files.length > 1) {
            files.splice(1)
          }

          const mergedFilesListLength = multiple ? currentFiles.length + files.length : files.length

          const filteredFilesByMax = files.slice(0, maxFiles - currentFiles.length)

          if (multiple && mergedFilesListLength > maxFiles) {
            multipleFilesErrorText = errors.maxFilesErrorText
          }

          const filteredFilesBySize = filteredFilesByMax.filter(
            file => sizeWithBase64Encoding(file.size) <= maxFileSize,
          )
          if (filteredFilesBySize.length < files.length) {
            if (files.length > 1) {
              multipleFilesErrorText = multipleFilesErrorText
                ? multipleFilesErrorText
                : errors.multipleFilesErrorText
            } else {
              throw new Error(errors.maxFileSizeErrorText)
            }
          }

          const filteredFilesByType = filteredFilesBySize.filter(file =>
            data.allowedTypes.includes(file.type),
          )
          if (filteredFilesByType.length < files.length) {
            if (files.length > 1) {
              multipleFilesErrorText = multipleFilesErrorText
                ? multipleFilesErrorText
                : errors.multipleFilesErrorText
            } else {
              throw new Error(errors.fileTypeErrorText)
            }
          }

          const { files: filteredFilesByOverallSize } = filteredFilesByType.reduce(
            (acc, file) => {
              const currentSize = sizeWithBase64Encoding(file.size)
              if (acc.overallSize + currentSize <= maxOverallFilesSize) {
                acc.overallSize += currentSize
                acc.files.push(file)
              } else {
                multipleFilesErrorText = errors.maxOverallFilesSizeErrorText
              }

              return acc
            },
            {
              files: [],
              overallSize: currentFiles.reduce((acc, file) => {
                return acc + sizeWithBase64Encoding(file.size)
              }, 0),
            },
          )

          const mergedFilesList = multiple
            ? [...currentFiles, ...filteredFilesByOverallSize]
            : files

          mergedFilesList.forEach(f => dt.items.add(f))
          if (fileInputRef.current) fileInputRef.current.files = dt.files

          setCurrentFiles(mergedFilesList)
          onChange?.(mergedFilesList)

          if (mergedFilesList.length === maxFiles) {
            onFullLoad()
          }

          multipleFilesErrorText &&
            enqueueSnackbar({
              message: multipleFilesErrorText,
              variant: 'error',
              duration: 3000,
            })
        } catch (err) {
          currentFiles.forEach(f => dt.items.add(f))
          if (fileInputRef.current) fileInputRef.current.files = dt.files

          if (currentFiles.length === maxFiles) {
            onFullLoad()
          }

          enqueueSnackbar({
            message: err.message,
            variant: 'error',
            duration: 3000,
          })
        } finally {
          onLoadEnd()
        }
      },
      [currentFiles, multiple, maxFiles, maxFileSize, onChange],
    )

    const handleRemoveFile = useCallback(
      index => {
        const newFileslist = Array.from(fileInputRef.current.files).filter((_, i) => i !== index)
        const dt = new DataTransfer()
        newFileslist.forEach(file => {
          dt.items.add(file)
        })
        fileInputRef.current.files = dt.files
        setCurrentFiles(newFileslist)
        onChange?.(newFileslist)
        onNotFullLoad()
      },
      [onChange],
    )

    const handleClick = useCallback(() => {
      if (!disabled) {
        fileInputRef.current?.click()
      }
    }, [disabled])

    const handleFocus = useCallback(() => {
      onFocus?.()
    }, [onFocus])

    const handleBlur = useCallback(() => {
      onBlur?.()
    }, [onBlur])

    const checkValidity = useCallback(() => {
      if (required && currentFiles.length === 0) {
        return false
      }
      if (multiple && currentFiles.length > maxFiles) {
        return false
      }
      const oversizedFiles = currentFiles.filter(file => file.size > maxFileSize)
      if (oversizedFiles.length > 0) {
        return false
      }
      return true
    }, [required, currentFiles, multiple, maxFiles, maxFileSize])

    useImperativeHandle(
      innerRef,
      () => ({
        getValue: () => currentFiles,
        getFiles: () => currentFiles,
        checkValidity,
        blur: handleBlur,
        focus: handleFocus,
      }),
      [currentFiles, onBlur, onFocus, checkValidity],
    )

    return (
      <div className={classNames(cls.fileInput, className)}>
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={allowedTypes}
          onLoadStart={onLoadStart}
          onLoadedData={onLoadEnd}
          onChange={handleFileSelect}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          className={cls.hiddenInput}
          {...otherProps}
        />
        {placeholder && (
          <InputLabel htmlFor={id} required={required} shrink>
            {placeholder}
          </InputLabel>
        )}
        {buttonLabel && (
          <Button type={'button'} onClick={handleClick} loading={isLoading} disabled={isFullLoad}>
            {buttonLabel}
          </Button>
        )}
        {requirementsInfoList}
        {currentFiles.length > 0 && (
          <>
            <Typography
              className={cls.placeholder}
            >{`${currentFiles.length} файл(ов) выбрано`}</Typography>
            <div className={cls.fileList}>
              {currentFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className={cls.fileItem}>
                  <div className={cls.fileInfo}>
                    <Typography className={cls.fileName}>{file.name}</Typography>
                    <Typography className={cls.fileSize}>
                      {formatFileSize(sizeWithBase64Encoding(file.size))}
                    </Typography>
                  </div>
                  <div className={classNames(cls.close)} onClick={() => handleRemoveFile(index)}>
                    <Icon>close</Icon>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    )
  },
)

InputFile.propTypes = {
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  data: PropTypes.shape({
    id: PropTypes.string,
    info: PropTypes.shape({
      fileSize: PropTypes.string,
      overallFilesSize: PropTypes.string,
    }),
    errors: PropTypes.shape({
      errorText: PropTypes.string,
      fileTypeErrorText: PropTypes.string,
      maxFilesErrorText: PropTypes.string,
      maxFileSizeErrorText: PropTypes.string,
      multipleFilesErrorText: PropTypes.string,
      maxOverallFilesSizeErrorText: PropTypes.string,
    }),
    placeholder: PropTypes.string,
    maxFiles: PropTypes.number,
    maxFileSize: PropTypes.number,
    required: PropTypes.bool,
    multiple: PropTypes.bool,
    allowedTypes: PropTypes.arrayOf(PropTypes.string),
    buttonLabel: PropTypes.string,
  }),
}
